import React, { useEffect, useState } from 'react';
import AnimatedSection from '../AnimatedSection';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import {
  PARTNER_CATEGORY_LABELS,
  PARTNER_STATUS_LABELS,
  PARTNER_STATUS_TONES,
  PARTNER_TIER_LABELS,
} from '../../constants/partnerNetwork';
import { changePartnerPassword } from '../../services/partnerPortal';
import { formatDate, formatRate } from '../../utils/partnerFormat';
import { Alert, StatusBadge, inputClass } from '../partner-network/ui';

const PartnerProfile: React.FC = () => {
  const { partner, saveProfile } = usePartnerAuth();

  const [form, setForm] = useState({
    fullName: '',
    companyName: '',
    phone: '',
    country: '',
    city: '',
    website: '',
    linkedinUrl: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!partner) return;
    setForm({
      fullName: partner.fullName,
      companyName: partner.companyName,
      phone: partner.phone,
      country: partner.country,
      city: partner.city,
      website: partner.website,
      linkedinUrl: partner.linkedinUrl,
    });
  }, [partner]);

  const set = <K extends keyof typeof form>(key: K, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (!form.fullName.trim()) {
      setError('Your name cannot be empty.');
      return;
    }

    try {
      setSaving(true);
      await saveProfile(form);
      setNotice('Your details have been saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your details');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordNotice(null);

    if (passwords.next.length < 8) {
      setPasswordError('Your new password must be at least 8 characters.');
      return;
    }
    if (passwords.next !== passwords.confirm) {
      setPasswordError('The two new passwords do not match.');
      return;
    }

    try {
      setChangingPassword(true);
      await changePartnerPassword(passwords.current, passwords.next);
      setPasswords({ current: '', next: '', confirm: '' });
      setPasswordNotice('Your password has been changed.');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Could not change the password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (!partner) return null;

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Your profile
            </h1>
            <p className="text-sm text-muted">
              Keep your contact details current so we can reach you about your referrals.
            </p>
          </div>
        </AnimatedSection>

        {/* Read-only partnership terms */}
        <div className="mb-6 rounded-2xl border border-surface-border bg-surface-card p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
            Your partnership
          </h2>
          <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted">Partner ID</dt>
              <dd className="font-mono text-primary-400">{partner.partnerId}</dd>
            </div>
            <div>
              <dt className="text-muted">Status</dt>
              <dd>
                <StatusBadge
                  label={PARTNER_STATUS_LABELS[partner.status]}
                  tone={PARTNER_STATUS_TONES[partner.status]}
                />
              </dd>
            </div>
            <div>
              <dt className="text-muted">Commission rate</dt>
              <dd className="text-white">{formatRate(partner.commissionRate)}</dd>
            </div>
            <div>
              <dt className="text-muted">Partner since</dt>
              <dd className="text-white">{formatDate(partner.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-muted">Category</dt>
              <dd className="text-white">
                {partner.category ? PARTNER_CATEGORY_LABELS[partner.category] : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-muted">Tier</dt>
              <dd className="text-white">
                {partner.tier ? PARTNER_TIER_LABELS[partner.tier] : '—'}
              </dd>
            </div>
          </dl>
          <p className="mt-5 border-t border-surface-border pt-4 text-xs text-muted">
            Your Partner ID, commission rate and tier are set by Nexasolutra. Get in touch
            if any of them look wrong.
          </p>
        </div>

        {/* Editable details */}
        <form
          onSubmit={handleSave}
          className="mb-6 rounded-2xl border border-surface-border bg-surface-card p-6"
        >
          <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-muted">
            Your details
          </h2>

          {error && (
            <Alert tone="error" className="mb-5">
              {error}
            </Alert>
          )}
          {notice && !error && (
            <Alert tone="success" className="mb-5">
              {notice}
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-2 block text-sm text-muted">Full name</label>
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => set('fullName', e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-sm text-muted">Email</label>
              <input
                type="email"
                value={partner.email}
                disabled
                className={`${inputClass} cursor-not-allowed opacity-60`}
              />
              <p className="mt-2 text-xs text-muted">
                Your email is your sign-in. Contact us if it needs to change.
              </p>
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Company</label>
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => set('companyName', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Country</label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => set('country', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Website</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => set('website', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">LinkedIn</label>
              <input
                type="url"
                value={form.linkedinUrl}
                onChange={(e) => set('linkedinUrl', e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary mt-6 py-2.5 px-5 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save details'}
          </button>
        </form>

        {/* Password */}
        <form
          onSubmit={handlePasswordChange}
          className="rounded-2xl border border-surface-border bg-surface-card p-6"
        >
          <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-muted">
            Change password
          </h2>

          {passwordError && (
            <Alert tone="error" className="mb-5">
              {passwordError}
            </Alert>
          )}
          {passwordNotice && !passwordError && (
            <Alert tone="success" className="mb-5">
              {passwordNotice}
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-2 block text-sm text-muted">Current password</label>
              <input
                type="password"
                value={passwords.current}
                onChange={(e) =>
                  setPasswords({ ...passwords, current: e.target.value })
                }
                autoComplete="current-password"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">New password</label>
              <input
                type="password"
                value={passwords.next}
                onChange={(e) => setPasswords({ ...passwords, next: e.target.value })}
                autoComplete="new-password"
                placeholder="At least 8 characters"
                className={inputClass}
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Confirm new password</label>
              <input
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                autoComplete="new-password"
                className={inputClass}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={changingPassword}
            className="btn-secondary mt-6 py-2.5 px-5 disabled:opacity-60"
          >
            {changingPassword ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default PartnerProfile;
